import React, { useEffect, useState } from "react";
import api from "../services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUtensils, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// ─── Macro calculation (mirrors NutritionModal logic) ────────────────────────

function effectiveG(item) {
  const qty = item.quantity_g || 0;
  if (item.unit === "ml") return qty * (item.food?.density_g_per_ml ?? 1.0);
  return qty;
}

function calcMacros(meals, weightKg) {
  let pa = 0, pv = 0, carbs = 0, fat = 0;
  for (const meal of (meals || [])) {
    for (const item of (meal.items || [])) {
      if (!item.food) continue;
      const r = effectiveG(item) / 100;
      pa    += (item.food.protein_animal_g || 0) * r;
      pv    += (item.food.protein_veg_g    || 0) * r;
      carbs += (item.food.carbs_g          || 0) * r;
      fat   += (item.food.fat_g            || 0) * r;
    }
  }
  const pt = pa + pv;
  const total = pt * 4 + carbs * 4 + fat * 9;
  const pct   = (kcal) => total > 0 ? Math.round(kcal / total * 100) : 0;
  return {
    protein: Math.round(pt),
    carbs:   Math.round(carbs),
    fat:     Math.round(fat),
    kcal:    Math.round(total),
    proteinPct: pct(pt * 4),
    carbsPct:   pct(carbs * 4),
    fatPct:     pct(fat * 9),
    gkg: weightKg ? (pt / weightKg).toFixed(1) : null,
  };
}

// ─── Mini macro pill ─────────────────────────────────────────────────────────

function MacroPill({ label, value, unit, pct, color }) {
  return (
    <div style={{
      flex: 1,
      minWidth: 0,
      background: "rgba(255,255,255,0.04)",
      borderRadius: 12,
      padding: "10px 12px",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      borderTop: `2px solid ${color}`,
    }}>
      <span style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 500, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1 }}>
        {value}<span style={{ fontSize: 12, fontWeight: 400, color: "var(--text-secondary)", marginLeft: 2 }}>{unit}</span>
      </span>
      {pct != null && (
        <span style={{ fontSize: 11, color, fontWeight: 600 }}>{pct}%</span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DietBannerCard({ onOpen }) {
  const [diet, setDiet]       = useState(null);   // null = loading, false = none
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/nutrition/diets")
      .then((r) => {
        const latest = r.data?.[0] ?? false;
        setDiet(latest);
      })
      .catch(() => setDiet(false))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !diet) return null;

  const m = calcMacros(diet.meals, diet.weight_kg);

  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      style={{
        margin: "0 0 20px",
        padding: "18px 20px",
        borderRadius: 20,
        border: "1px solid rgba(159,225,203,0.22)",
        background: "linear-gradient(135deg, var(--surface-strong), var(--surface-primary))",
        boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 18px 40px rgba(0,0,0,0.45)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.35)"; }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: "rgba(159,225,203,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FontAwesomeIcon icon={faUtensils} style={{ color: "var(--feedback-success)", fontSize: 14 }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 12, color: "var(--feedback-success)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Nutrição
            </p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
              {diet.name || `Dieta ${diet.date}`}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--feedback-success)" }}>{m.kcal} kcal</span>
          <FontAwesomeIcon icon={faChevronRight} style={{ color: "var(--text-secondary)", fontSize: 12 }} />
        </div>
      </div>

      {/* Macro pills */}
      <div style={{ display: "flex", gap: 8 }}>
        <MacroPill label="Proteína" value={m.protein} unit="g" pct={m.proteinPct} color="#9fe1cb" />
        <MacroPill label="Carboidrato" value={m.carbs} unit="g" pct={m.carbsPct} color="#63b3ed" />
        <MacroPill label="Lipídio" value={m.fat} unit="g" pct={m.fatPct} color="#f6c90e" />
        {m.gkg && (
          <MacroPill label="Prot/kg" value={m.gkg} unit="g/kg" pct={null} color="#b794f4" />
        )}
      </div>

      {/* Footer hint */}
      <p style={{ margin: "12px 0 0", fontSize: 12, color: "var(--text-secondary)", textAlign: "right" }}>
        Toque para ver a dieta completa
      </p>
    </div>
  );
}
